// ============================================================================
// Estimated Delivery Report
// ----------------------------------------------------------------------------
// Active order forms grouped by their expected delivery (estimated completion)
// date. Daily / Weekly / Monthly views, grouped Customer-wise or Goldsmith-wise
// with a constant Page-Number sub-grouping. Groups are collapsible (collapsed
// by default) and show order-form + page-number counts. Each period surfaces its
// own totals. Clicking an order opens the order page; clicking a page number
// deep-links to Orders filtered by that page.
//
// Visual style: "Board / Cards" — each page sub-group is a card inside the
// customer/goldsmith group. Weeks start on SUNDAY.
// ============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { OrderCard } from './Orders';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  CalendarClock, CalendarRange, ChevronLeft, ChevronRight, ChevronDown,
  Users, Hammer, Hash, Package, ExternalLink, Inbox, Loader2, CalendarDays,
  CircleSlash, Check, AlertTriangle, Activity, Printer,
} from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// Sunday-first week labels.
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TONES = ['#6C8FD1', '#7FB5A6', '#B49BE0', '#E0A45E', '#D98BA8', '#8AAE68', '#C98A6B'];

// IMPORTANT: format using LOCAL date parts (NOT toISOString, which uses UTC and
// shifts the date by a day in non-UTC timezones — the cause of the off-by-one
// weekday + "monthly click opens previous day" bugs).
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseISO(s) { const [y, m, d] = (s || '').slice(0, 10).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); }
function fmtLong(s) {
  const d = parseISO(s);
  const wd = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
  return `${wd}, ${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
}
function fmtShort(s) { const d = parseISO(s); return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()].slice(0, 3)}`; }
function addDays(s, n) { const d = parseISO(s); d.setDate(d.getDate() + n); return toISO(d); }
function addMonths(s, n) { const d = parseISO(s); d.setMonth(d.getMonth() + n); return toISO(d); }
// Sunday-start week.
function weekStartOf(s) { const d = parseISO(s); d.setDate(d.getDate() - d.getDay()); return toISO(d); }
function toneFor(key) { const s = String(key || ''); let h = 0; for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % TONES.length; return TONES[h]; }
function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// Distinct non-null page-number count across a flat order list.
function distinctPages(list) {
  const s = new Set();
  for (const o of list) if (o.reference_no != null) s.add(o.reference_no);
  return s.size;
}

// ----------------------------------------------------------------------------
// Print / PDF export — builds a clean, fully-expanded standalone HTML document
// for the current view and opens the browser print dialog (Save as PDF).
// ----------------------------------------------------------------------------
function escHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function groupsToHtml(groups, { usesRef, refLabel, danger, showEta }) {
  if (!groups.length) return '<div class="empty">No order forms in this section.</div>';
  return groups.map((g) => {
    const pages = g.pageGroups.map((p) => {
      const dates = showEta ? [...new Set(p.orders.map((o) => o.estimated_completion).filter(Boolean))] : [];
      const shared = showEta && dates.length === 1 ? dates[0] : null;
      const perOrder = showEta && !shared;
      const hdr = usesRef ? (p.ref != null ? `${escHtml(refLabel)} ${escHtml(p.ref)}` : `No ${escHtml(refLabel.toLowerCase())}`) : 'Orders';
      const sharedTxt = shared ? ` &middot; <span class="date">${fmtShort(shared)} '${String(parseISO(shared).getFullYear()).slice(-2)}</span>` : '';
      const orders = p.orders.map((o) => {
        const stageTxt = (o.active_steps && o.active_steps.length)
          ? o.active_steps.map((s) => `${s.label} ${s.sla_status === 'waiting' ? 'Waiting' : 'Active'}`).join(', ')
          : (o.stage || '');
        const st = stageTxt ? `<span class="st">${escHtml(stageTxt)}</span>` : '';
        const dt = perOrder && o.estimated_completion ? ` <span class="date">${fmtShort(o.estimated_completion)} '${String(parseISO(o.estimated_completion).getFullYear()).slice(-2)}</span>` : '';
        return `<span class="ord">${escHtml(o.order_no)}${st}${dt}</span>`;
      }).join('');
      return `<div class="pagerow"><div class="pagehdr">${hdr}${sharedTxt} <span class="cnt">(${p.orders.length})</span></div><div class="ords">${orders}</div></div>`;
    }).join('');
    return `<div class="group${danger ? ' red' : ''}"><div class="gh"><span>${escHtml(g.name)}</span><span class="gc">${g.orders.length} order forms &middot; ${g.pageCount} ${escHtml(refLabel.toLowerCase())}</span></div>${pages}</div>`;
  }).join('');
}

function printReport(opts) {
  const {
    periodLabel, viewName, groupBy, usesRef, refLabel = 'Page No.',
    groups = [], totalOrders = 0, totalPages = 0,
    overdue = [], noDateCount = 0, monthly = false, dayTotals = {},
  } = opts;
  const now = new Date();
  const genStr = `${String(now.getDate()).padStart(2, '0')} ${MONTHS[now.getMonth()].slice(0, 3)} ${now.getFullYear()}, ` +
    now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const totalsHtml = `
    <div class="totals">
      <span class="pill">${totalOrders} order forms</span>
      ${usesRef ? `<span class="pill">${totalPages} ${escHtml(refLabel.toLowerCase())}</span>` : ''}
      ${overdue.length ? `<span class="pill red">${overdue.length} overdue</span>` : ''}
    </div>`;

  let body = '';
  if (monthly) {
    const keys = Object.keys(dayTotals).sort();
    const rows = keys.length
      ? keys.map((d) => `<tr><td>${fmtLong(d)}</td><td class="num">${dayTotals[d].orders}</td><td class="num">${usesRef ? dayTotals[d].pages : '&mdash;'}</td></tr>`).join('')
      : '<tr><td colspan="3" class="empty">No scheduled deliveries this month.</td></tr>';
    body += `<table><thead><tr><th>Date</th><th class="num">Order forms</th><th class="num">${escHtml(refLabel)}</th></tr></thead><tbody>${rows}</tbody></table>`;
  } else {
    if (overdue.length) {
      body += `<h2 class="sec red">Overdue deliveries — carried to today</h2>${groupsToHtml(pivot(overdue, groupBy), { usesRef, refLabel, danger: true, showEta: true })}`;
    }
    body += `<h2 class="sec">${escHtml(groupBy === 'customer' ? 'By Customer' : 'By Goldsmith')}</h2>${groupsToHtml(groups, { usesRef, refLabel, danger: false, showEta: false })}`;
  }
  if (noDateCount) body += `<div class="note">+ ${noDateCount} order form(s) with no delivery date set (not listed).</div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Estimated Delivery Report — ${escHtml(periodLabel)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:'Figtree',Arial,sans-serif;color:#1b2f45;margin:22px;font-size:13px}
    .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1b2f45;padding-bottom:10px}
    .hd h1{font-size:18px;margin:0} .hd .sub{color:#64748b;font-size:12px;margin-top:3px} .meta{text-align:right;font-size:11px;color:#64748b}
    .totals{display:flex;gap:8px;margin:12px 0 4px;flex-wrap:wrap}
    .pill{border:1px solid #e2e8f0;border-radius:6px;padding:5px 11px;font-size:12px;font-weight:600;background:#f8fafc}
    .pill.red{border-color:#fecdd3;background:#fff1f2;color:#be123c}
    h2.sec{font-size:13px;margin:16px 0 8px;padding-bottom:4px;border-bottom:1px solid #e2e8f0} h2.sec.red{color:#be123c;border-color:#fecdd3}
    .group{border:1px solid #e2e8f0;border-radius:8px;margin-bottom:9px;break-inside:avoid;page-break-inside:avoid}
    .group.red{border-color:#fecdd3}
    .gh{background:#f8fafc;padding:7px 11px;font-weight:700;font-size:13px;display:flex;justify-content:space-between;border-bottom:1px solid #e2e8f0}
    .group.red .gh{background:#fff1f2;color:#be123c} .gh .gc{font-weight:500;color:#64748b;font-size:11px}
    .pagerow{padding:7px 11px;border-bottom:1px dashed #eef2f7} .pagerow:last-child{border-bottom:none}
    .pagehdr{font-size:11.5px;font-weight:700;color:#4338ca;margin-bottom:4px} .pagehdr .cnt{color:#94a3b8;font-weight:500}
    .date{color:#b45309;font-weight:700}
    .ord{font-family:'Source Code Pro',monospace;font-size:11px;display:inline-block;border:1px solid #e2e8f0;border-radius:4px;padding:2px 6px;margin:2px 5px 2px 0}
    .ord .st{color:#64748b;font-size:9px;text-transform:uppercase;margin-left:5px}
    table{width:100%;border-collapse:collapse;font-size:12px} th,td{border:1px solid #e2e8f0;padding:6px 9px;text-align:left} th{background:#f8fafc} td.num,th.num{text-align:right;width:120px}
    .note{font-size:11px;color:#64748b;margin-top:10px} .empty{color:#94a3b8;padding:10px;font-size:12px}
    @media print{body{margin:12mm} .noprint{display:none}}
  </style></head><body>
    <div class="hd">
      <div><h1>Estimated Delivery Report</h1><div class="sub">${escHtml(viewName)} &middot; ${escHtml(periodLabel)}</div></div>
      <div class="meta">Generated<br>${escHtml(genStr)}</div>
    </div>
    ${totalsHtml}
    ${body}
  </body></html>`;

  const w = window.open('', '_blank', 'width=1040,height=820');
  if (!w) return false;
  w.document.open(); w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => { try { w.print(); } catch (e) { /* user can print manually */ } }, 450);
  return true;
}


// ----------------------------------------------------------------------------
// Pivot a flat order list into groups (customer|goldsmith) → page sub-groups.
// ----------------------------------------------------------------------------
function pivot(orders, groupBy) {
  const groups = new Map();
  for (const o of orders) {
    const key = groupBy === 'customer' ? (o.customer_id || '__none__') : (o.goldsmith_id || '__none__');
    const name = (groupBy === 'customer' ? o.customer_name : o.goldsmith_name) || 'Unassigned';
    let g = groups.get(key);
    if (!g) { g = { key, name, orders: [] }; groups.set(key, g); }
    g.orders.push(o);
  }
  const out = [];
  for (const g of groups.values()) {
    const pages = new Map();
    for (const o of g.orders) {
      const pk = o.reference_no != null ? String(o.reference_no) : '__nopage__';
      let p = pages.get(pk);
      if (!p) { p = { ref: o.reference_no, orders: [] }; pages.set(pk, p); }
      p.orders.push(o);
    }
    const pageGroups = [...pages.values()].sort((a, b) => {
      if (a.ref == null) return 1; if (b.ref == null) return -1; return a.ref - b.ref;
    });
    out.push({ ...g, pageGroups, pageCount: pageGroups.filter((p) => p.ref != null).length });
  }
  out.sort((a, b) => {
    if (a.key === '__none__') return 1; if (b.key === '__none__') return -1;
    return a.name.localeCompare(b.name);
  });
  return out;
}

// Small count pills reused across the report.
const CountPills = ({ orders, pages, usesRef, danger = false }) => (
  <span className="inline-flex items-center gap-1.5 shrink-0">
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${danger ? 'bg-rose-100 text-rose-700' : 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]'}`} title="Order forms">
      <Package size={11} /> {orders}
    </span>
    {usesRef && (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${danger ? 'bg-rose-100 text-rose-700' : 'bg-indigo-50 text-indigo-700'}`} title="Page numbers">
        <Hash size={11} /> {pages}
      </span>
    )}
  </span>
);

// Active production-checklist stage badge(s) — mirrors the Order Detail page's
// active step(s): "CAD Active", "Raw Material Active", "Quality Check Active",
// or "Ghat Waiting" (awaiting gold). Falls back to the coarse stage label.
const StageBadge = ({ order }) => {
  const steps = (order && order.active_steps) || [];
  if (steps.length) {
    return (
      <>
        {steps.map((s) => {
          const waiting = s.sla_status === 'waiting';
          return (
            <span
              key={s.step}
              className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9.5px] font-semibold ${waiting ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}
              title={`Active production stage: ${s.label}${waiting ? ' — waiting for gold' : ''}`}
              data-testid="edr-order-stage"
            >
              <Activity size={8} />{s.label} {waiting ? 'Waiting' : 'Active'}
            </span>
          );
        })}
      </>
    );
  }
  if (order && order.stage) {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1 py-0.5 text-[9.5px] font-semibold text-slate-500"
        title={`Stage: ${order.stage}`}
        data-testid="edr-order-stage"
      >
        <Activity size={8} className="text-slate-400" />{order.stage}
      </span>
    );
  }
  return null;
};

// Expected-delivery-date badge (used when a page-number's orders have mixed
// dates, or shown once on the page card when they all share one date).
const EtaBadge = ({ date, danger = false, testid }) => {
  if (!date) return null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${danger ? 'bg-rose-100 text-rose-700' : 'bg-amber-50 text-amber-700'}`}
      title={`Expected delivery: ${fmtLong(date)}`}
      data-testid={testid}
    >
      <CalendarClock size={9} /> {fmtShort(date)} '{String(parseISO(date).getFullYear()).slice(-2)}
    </span>
  );
};

// An order chip/row — opens the order in a new tab; shows its active stage and,
// when requested, its own expected delivery date.
const OrderLink = ({ o, danger = false, showEta = false }) => (
  <a
    href={`/orders/${o.id}`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`edr-order-${o.id}`}
    className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11.5px] transition-colors ${danger
      ? 'border-rose-300 bg-rose-50/60 text-rose-900 hover:border-rose-500 hover:bg-rose-100'
      : 'border-[var(--divider)] bg-white text-[var(--ink-navy)] hover:border-[var(--gold-accent)] hover:bg-[var(--jewellery-bg)]'}`}
    title={o.title || o.order_no}
  >
    {o.order_no}
    <StageBadge order={o} />
    {showEta && <EtaBadge date={o.estimated_completion} danger={danger} testid={`edr-order-eta-${o.id}`} />}
    <ExternalLink size={10} className="opacity-0 transition-opacity group-hover:opacity-70" />
  </a>
);

// A page-number chip — opens Orders filtered on that page in a new tab.
const PageChip = ({ refNo, refLabel, count }) => {
  if (refNo == null) {
    return <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"><CircleSlash size={11} /> No {refLabel.toLowerCase()}</span>;
  }
  return (
    <a
      href={`/orders?reference_no=${encodeURIComponent(refNo)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`edr-page-${refNo}`}
      className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-mono text-[11.5px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100"
      title={`Open Orders filtered by ${refLabel} ${refNo} (new tab)`}
    >
      <Hash size={10} />{refNo}
      {count != null && <span className="ml-0.5 rounded bg-white/70 px-1 text-[10px] text-indigo-600">{count}</span>}
    </a>
  );
};

// ----------------------------------------------------------------------------
// Grouped list — "Board / Cards" style (the finalised design).
// Each page sub-group renders as a card inside the customer/goldsmith group.
// ----------------------------------------------------------------------------
function GroupedList({ groups, usesRef, refLabel, refLabelShort, expanded, onToggle, onPage, groupBy, emptyHint, danger = false, showEta = false, canEdit = false, onUpdateCompletion }) {
  if (!groups.length) {
    return (
      <Card className="flex flex-col items-center gap-2 border-dashed px-4 py-10 text-center" data-testid="edr-empty-groups">
        <Inbox size={24} className="text-slate-300" />
        <div className="text-[13px] font-medium text-[var(--ink-navy)]">Nothing scheduled</div>
        <div className="text-[11.5px] text-slate-500">{emptyHint || 'No order forms are expected here.'}</div>
      </Card>
    );
  }
  const GroupIcon = groupBy === 'customer' ? Users : Hammer;

  return (
    <div className="space-y-3" data-testid="edr-groups">
      {groups.map((g) => {
        const open = expanded.has(g.key);
        const tone = danger ? '#e11d48' : (g.key === '__none__' ? '#94a3b8' : toneFor(g.key));
        return (
          <div key={g.key} className={`rounded-xl border bg-white shadow-sm ${danger ? 'border-rose-200' : 'border-[var(--divider)]'}`} data-testid={`edr-group-${g.key}`}>
            <button
              type="button"
              onClick={() => onToggle(g.key)}
              data-testid={`edr-group-toggle-${g.key}`}
              className="flex w-full items-center gap-3 rounded-t-xl px-4 py-3 text-left transition-colors hover:bg-[var(--paper-warm)]/60"
              style={{ background: open ? `${tone}12` : 'transparent' }}
            >
              <span className="grid h-8 w-8 place-items-center rounded-lg text-[11px] font-bold text-white" style={{ background: tone }}>{initials(g.name)}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-[14px] font-semibold text-[var(--ink-navy)]"><GroupIcon size={13} className="text-slate-400" /><span className="truncate">{g.name}</span></span>
              </span>
              <CountPills orders={g.orders.length} pages={g.pageCount} usesRef={usesRef} danger={danger} />
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
              <div className={`space-y-4 border-t p-3 ${danger ? 'border-rose-200' : 'border-[var(--divider)]'}`}>
                {g.pageGroups.map((p) => {
                  // If every order on this page shares one expected delivery
                  // date, show it once on the page header (overdue / no-date
                  // buckets); each card also shows its own editable ETA.
                  const dates = showEta ? [...new Set(p.orders.map((o) => o.estimated_completion).filter(Boolean))] : [];
                  const sharedDate = showEta && dates.length === 1 ? dates[0] : null;
                  return (
                    <div key={p.ref ?? 'none'} data-testid={`edr-pagegroup-${p.ref ?? 'none'}`}>
                      <div className="mb-2 flex items-center justify-between gap-1.5">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                          {usesRef ? <PageChip refNo={p.ref} refLabel={refLabel} count={null} />
                            : <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Orders</span>}
                          {sharedDate && <EtaBadge date={sharedDate} danger={danger} testid={`edr-page-eta-${p.ref ?? 'none'}`} />}
                        </div>
                        <span className="shrink-0 text-[11px] font-semibold text-slate-500">{p.orders.length}</span>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                        {p.orders.map((o) => (
                          <OrderCard
                            key={o.id}
                            order={o}
                            canEdit={canEdit}
                            canToggleSla={false}
                            onUpdateCompletion={onUpdateCompletion}
                            refLabel={refLabel}
                            refLabelShort={refLabelShort}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Multi-select filter popover (client-side).
// ----------------------------------------------------------------------------
function FilterPopover({ label, icon: Icon, options, selected, onChange, testid }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${selected.length ? 'border-[var(--gold-accent)] bg-[var(--jewellery-bg)] text-[var(--ink-navy)]' : 'border-[var(--divider)] bg-white text-slate-600 hover:bg-[var(--paper-warm)]'}`}
        >
          <Icon size={13} /> {label}{selected.length ? ` · ${selected.length}` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-0">
        <div className="max-h-72 overflow-auto p-1.5">
          {options.length === 0 && <div className="px-2 py-3 text-center text-[12px] text-slate-400">No options</div>}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] text-[var(--ink-navy)] hover:bg-[var(--paper-warm)]"
            >
              <span className={`grid h-4 w-4 place-items-center rounded border ${selected.includes(opt.value) ? 'border-[var(--gold-accent)] bg-[var(--gold-accent)] text-white' : 'border-slate-300'}`}>
                {selected.includes(opt.value) && <Check size={11} />}
              </span>
              <span className="min-w-0 flex-1 truncate">{opt.label}</span>
              <span className="text-[11px] text-slate-400">{opt.count}</span>
            </button>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-[var(--divider)] p-1.5">
            <button type="button" onClick={() => onChange([])} className="w-full rounded px-2 py-1 text-[12px] font-medium text-slate-500 hover:bg-[var(--paper-warm)]">Clear</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function EstimatedDeliveryReport() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Estimated Delivery Report" subtitle="Order forms grouped by their expected delivery date.">
      <ReportBody companyId={effectiveCompanyId} />
    </AppShell>
  );
}

function ReportBody({ companyId }) {
  const navigate = useNavigate();
  const { isSuperAdmin, isCompanyAdmin, isSalesRole, hasPermission } = useAuth();
  // Mirror the Orders page permission for inline ETA editing.
  const canEdit = !!(isSuperAdmin || isCompanyAdmin || hasPermission('orders.edit') || hasPermission('orders.edit_header'));
  // Print / Export is gated by the dedicated report permission; admins and
  // customer-scoped Sales roles may always print their own report.
  const canPrint = !!(isSuperAdmin || isCompanyAdmin || isSalesRole || hasPermission('reports.estimated_delivery.print'));

  const [view, setView] = useState('daily'); // daily | weekly | monthly
  const [anchor, setAnchor] = useState(() => toISO(new Date()));
  const [groupBy, setGroupBy] = useState('customer');
  const [selCust, setSelCust] = useState([]);
  const [selGold, setSelGold] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  // ``soft`` re-fetches the report WITHOUT blanking the view (no full "Loading…"
  // card, expanded groups preserved) — used after an inline ETA edit so the
  // order silently re-buckets to its new day/week/group.
  const load = useCallback(async ({ soft = false } = {}) => {
    if (soft) setRefreshing(true); else { setLoading(true); setError(null); }
    try {
      const params = { view, date: anchor, group_by: groupBy };
      if (companyId) params.company_id = companyId;
      const { data } = await api.get('/reports/estimated-delivery', { params });
      setPayload(data);
      if (soft) setError(null);
    } catch (e) {
      if (soft) {
        toast.error('Could not refresh the report');
      } else {
        setError(e?.response?.data?.detail || 'Could not load the report.');
        setPayload(null);
      }
    } finally {
      if (soft) setRefreshing(false); else setLoading(false);
    }
  }, [companyId, view, anchor, groupBy]);

  useEffect(() => { load(); }, [load]);
  // Collapse all groups whenever the view / date / grouping changes.
  useEffect(() => { setExpanded(new Set()); }, [view, anchor, groupBy]);

  // Inline ETA edit from an EDR card — same endpoint + history logging as the
  // Orders page, then a SOFT refresh so the order moves to its new bucket.
  const onUpdateCompletion = useCallback(async (orderId, value) => {
    try {
      await api.put(`/orders/${orderId}`, { estimated_completion: value || null });
      toast.success(value ? 'Estimated delivery updated' : 'Estimated delivery cleared');
      await load({ soft: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update estimated delivery');
      throw e; // keep the card's editor open on failure
    }
  }, [load]);

  const usesRef = !!payload?.uses_reference_numbers;
  const refLabel = payload?.reference_label || 'Page No.';
  const refLabelShort = useMemo(() => (refLabel.split(' ')[0] || refLabel).replace(/\.$/, ''), [refLabel]);
  const allOrders = payload?.orders || [];
  const noDate = payload?.no_date_orders || [];
  const overdueAll = payload?.overdue_orders || [];
  const today = payload?.today || toISO(new Date());

  // Client-side filtering (keeps filter option lists complete + stable).
  const applyFilters = useCallback((list) => list.filter((o) => {
    if (selCust.length && !selCust.includes(o.customer_id || '__none__')) return false;
    if (selGold.length && !selGold.includes(o.goldsmith_id || '__none__')) return false;
    return true;
  }), [selCust, selGold]);

  const custOptions = useMemo(() => {
    const m = new Map();
    for (const o of [...allOrders, ...noDate]) {
      const v = o.customer_id || '__none__';
      const label = o.customer_name || 'Unassigned';
      const e = m.get(v) || { value: v, label, count: 0 }; e.count += 1; m.set(v, e);
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allOrders, noDate]);
  const goldOptions = useMemo(() => {
    const m = new Map();
    for (const o of [...allOrders, ...noDate]) {
      const v = o.goldsmith_id || '__none__';
      const label = o.goldsmith_name || 'Unassigned';
      const e = m.get(v) || { value: v, label, count: 0 }; e.count += 1; m.set(v, e);
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allOrders, noDate]);

  const onPage = useCallback((refNo) => {
    window.open(`/orders?reference_no=${encodeURIComponent(refNo)}`, '_blank', 'noopener');
  }, []);
  const toggle = useCallback((key) => setExpanded((prev) => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n;
  }), []);

  // Per-date totals computed from the FILTERED orders so counts match what's shown.
  const dayTotals = useMemo(() => {
    const map = {};
    for (const o of applyFilters(allOrders)) {
      const k = o.estimated_completion; if (!k) continue;
      const d = map[k] || { orders: 0, pages: new Set() };
      d.orders += 1; if (o.reference_no != null) d.pages.add(o.reference_no); map[k] = d;
    }
    const out = {}; for (const k of Object.keys(map)) out[k] = { orders: map[k].orders, pages: map[k].pages.size };
    return out;
  }, [allOrders, applyFilters]);

  const filteredOrders = useMemo(() => applyFilters(allOrders), [allOrders, applyFilters]);
  const filteredNoDate = useMemo(() => applyFilters(noDate), [noDate, applyFilters]);
  const filteredOverdue = useMemo(() => applyFilters(overdueAll), [overdueAll, applyFilters]);
  const overdueCounts = useMemo(() => ({
    orders: filteredOverdue.length,
    pages: new Set(filteredOverdue.filter((o) => o.reference_no != null).map((o) => o.reference_no)).size,
  }), [filteredOverdue]);

  const navLabel = view === 'daily' ? fmtLong(anchor)
    : view === 'weekly' ? `${fmtShort(weekStartOf(anchor))} — ${fmtShort(addDays(weekStartOf(anchor), 6))}, ${parseISO(anchor).getFullYear()}`
    : `${MONTHS[parseISO(anchor).getMonth()]} ${parseISO(anchor).getFullYear()}`;
  const step = (dir) => {
    if (view === 'daily') setAnchor((a) => addDays(a, dir));
    else if (view === 'weekly') setAnchor((a) => addDays(a, dir * 7));
    else setAnchor((a) => addMonths(a, dir));
  };

  const handlePrint = useCallback(() => {
    const base = { groupBy, usesRef, refLabel, noDateCount: filteredNoDate.length, today };
    if (view === 'monthly') {
      const first = parseISO(anchor); first.setDate(1);
      const monthOrders = filteredOrders.filter((o) => {
        if (!o.estimated_completion) return false;
        const d = parseISO(o.estimated_completion);
        return d.getFullYear() === first.getFullYear() && d.getMonth() === first.getMonth();
      });
      printReport({ ...base, monthly: true, viewName: 'Monthly', periodLabel: navLabel, dayTotals,
        totalOrders: monthOrders.length, totalPages: distinctPages(monthOrders),
        overdue: filteredOverdue });
    } else if (view === 'weekly') {
      const start = weekStartOf(anchor); const end = addDays(start, 6);
      const weekOrders = filteredOrders.filter((o) => o.estimated_completion && o.estimated_completion >= start && o.estimated_completion <= end);
      const showOv = today >= start && today <= end;
      printReport({ ...base, viewName: 'Weekly', periodLabel: navLabel, groups: pivot(weekOrders, groupBy),
        totalOrders: weekOrders.length, totalPages: distinctPages(weekOrders),
        overdue: showOv ? filteredOverdue : [] });
    } else {
      const dayOrders = filteredOrders.filter((o) => o.estimated_completion === anchor);
      printReport({ ...base, viewName: 'Daily', periodLabel: navLabel, groups: pivot(dayOrders, groupBy),
        totalOrders: dayOrders.length, totalPages: distinctPages(dayOrders),
        overdue: anchor === today ? filteredOverdue : [] });
    }
  }, [view, anchor, groupBy, usesRef, refLabel, navLabel, dayTotals, filteredOrders, filteredOverdue, filteredNoDate, today]);

  return (
    <div className="space-y-4" data-testid="estimated-delivery-report">
      {/* Control bar */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="inline-flex rounded-lg border border-[var(--divider)] bg-white p-0.5 shadow-sm" data-testid="edr-view-toggle">
          {['daily', 'weekly', 'monthly'].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              data-testid={`edr-view-${v}`}
              className={`rounded-md px-3 py-1.5 text-[13px] font-semibold capitalize transition-colors ${view === v ? 'bg-[var(--ink-navy)] text-white shadow-sm' : 'text-slate-600 hover:bg-[var(--paper-warm)]'}`}
            >{v}</button>
          ))}
        </div>

        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 w-8 bg-white p-0" onClick={() => step(-1)} data-testid="edr-prev" aria-label="Previous"><ChevronLeft size={15} /></Button>
          <span className="min-w-[200px] text-center text-[13.5px] font-semibold text-[var(--ink-navy)]" data-testid="edr-period-label">{navLabel}</span>
          <Button variant="outline" size="sm" className="h-8 w-8 bg-white p-0" onClick={() => step(1)} data-testid="edr-next" aria-label="Next"><ChevronRight size={15} /></Button>
          <Button variant="outline" size="sm" className="h-8 bg-white px-2 text-[12px]" onClick={() => setAnchor(toISO(new Date()))} data-testid="edr-today">Today</Button>
        </div>

        {view !== 'monthly' && (
          <div className="inline-flex rounded-lg border border-[var(--divider)] bg-white p-0.5 shadow-sm" data-testid="edr-groupby-toggle">
            {[{ k: 'customer', l: 'Customer', i: Users }, { k: 'goldsmith', l: 'Goldsmith', i: Hammer }].map(({ k, l, i: I }) => (
              <button key={k} type="button" onClick={() => setGroupBy(k)} data-testid={`edr-groupby-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors ${groupBy === k ? 'bg-[var(--ink-navy)] text-white shadow-sm' : 'text-slate-600 hover:bg-[var(--paper-warm)]'}`}>
                <I size={13} /> {l}
              </button>
            ))}
          </div>
        )}

        {view !== 'monthly' && (
          <div className="inline-flex items-center gap-2">
            <FilterPopover label="Customers" icon={Users} options={custOptions} selected={selCust} onChange={setSelCust} testid="edr-filter-customers" />
            <FilterPopover label="Goldsmiths" icon={Hammer} options={goldOptions} selected={selGold} onChange={setSelGold} testid="edr-filter-goldsmiths" />
          </div>
        )}

        {refreshing && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500" data-testid="edr-refreshing">
            <Loader2 size={13} className="animate-spin text-[var(--gold-accent)]" /> Refreshing…
          </span>
        )}
        {canPrint && (
        <Button
          variant="outline"
          size="sm"
          className={`${refreshing ? 'ml-2' : 'ml-auto'} h-8 gap-1.5 bg-white px-2.5 text-[12.5px] font-semibold text-[var(--ink-navy)] hover:border-[var(--gold-accent)] hover:bg-[var(--jewellery-bg)]`}
          onClick={handlePrint}
          data-testid="edr-print-btn"
          disabled={loading || !!error}
          title="Print or save this view as a PDF"
        >
          <Printer size={14} /> Print / PDF
        </Button>
        )}
      </div>

      {loading ? (
        <Card className="flex items-center justify-center gap-2 px-4 py-16 text-slate-500" data-testid="edr-loading"><Loader2 size={18} className="animate-spin" /> Loading…</Card>
      ) : error ? (
        <Card className="flex flex-col items-center gap-3 border-rose-200 bg-rose-50/40 px-4 py-12 text-center" data-testid="edr-error">
          <div className="text-sm font-medium text-rose-700">{error}</div>
          <button onClick={load} className="rounded-md bg-[var(--ink-navy)] px-3 py-1.5 text-[13px] font-semibold text-white">Try again</button>
        </Card>
      ) : view === 'monthly' ? (
        <MonthlyView anchor={anchor} orders={filteredOrders} dayTotals={dayTotals} usesRef={usesRef} noDateCount={filteredNoDate.length}
          onPickDay={(d) => { setAnchor(d); setView('daily'); }} overdueCounts={overdueCounts} today={today} />
      ) : view === 'weekly' ? (
        <WeeklyView anchor={anchor} orders={filteredOrders} dayTotals={dayTotals} groupBy={groupBy}
          usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={toggle} onPage={onPage}
          noDate={filteredNoDate} onPickDate={setAnchor} overdue={filteredOverdue} today={today}
          canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
      ) : (
        <DailyView anchor={anchor} orders={filteredOrders} dayTotals={dayTotals} groupBy={groupBy}
          usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={toggle} onPage={onPage}
          noDate={filteredNoDate} onPickDate={setAnchor} overdue={filteredOverdue} today={today}
          canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
      )}
    </div>
  );
}

// Period summary banner (daily / weekly / monthly) — shows total order forms +
// total distinct page numbers expected in that period.
const SummaryBanner = ({ icon: Icon = CalendarClock, title, subtitle, orders, pages, usesRef }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--divider)] bg-gradient-to-r from-[var(--ink-navy)] to-[#1b2f45] px-4 py-3 text-white shadow-sm" data-testid="edr-summary-banner">
    <div className="flex items-center gap-2.5">
      <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10"><Icon size={18} className="text-[var(--gold-accent)]" /></span>
      <div>
        <div className="text-[15px] font-semibold leading-tight">{title}</div>
        <div className="text-[11.5px] text-white/60">{subtitle}</div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-[13px] font-semibold" data-testid="edr-banner-orders"><Package size={14} className="text-[var(--gold-accent)]" /> {orders || 0} order forms</span>
      {usesRef && <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-[13px] font-semibold" data-testid="edr-banner-pages"><Hash size={14} className="text-[var(--gold-accent)]" /> {pages || 0} page nos</span>}
    </div>
  </div>
);

function NoDateBucket({ noDate, usesRef, refLabel, refLabelShort, expanded, onToggle, onPage, groupBy, canEdit, onUpdateCompletion }) {
  if (!noDate.length) return null;
  const groups = pivot(noDate, groupBy);
  const open = expanded.has('__nodate__');
  return (
    <div data-testid="edr-nodate-bucket">
      <button
        type="button"
        onClick={() => onToggle('__nodate__')}
        data-testid="edr-nodate-toggle"
        className="flex w-full items-center gap-2.5 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-3.5 py-2.5 text-left transition-colors hover:bg-amber-50"
      >
        <CircleSlash size={16} className="shrink-0 text-amber-500" />
        <span className="flex-1 text-[13.5px] font-semibold text-amber-800">No delivery date set</span>
        <CountPills orders={noDate.length} pages={distinctPages(noDate)} usesRef={usesRef} />
        <ChevronDown size={16} className={`text-amber-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2">
          <GroupedList groups={groups} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        </div>
      )}
    </div>
  );
}

// Overdue bucket — active order forms past their estimated delivery date that
// have NOT been completed. Rolls forward onto "today" until received/cancelled.
// Red styling; collapsible; collapsed by default; counts shown on the header.
function OverdueBucket({ overdue, usesRef, refLabel, refLabelShort, expanded, onToggle, onPage, groupBy, canEdit, onUpdateCompletion }) {
  if (!overdue.length) return null;
  const groups = pivot(overdue, groupBy);
  const open = expanded.has('__overdue__');
  return (
    <div data-testid="edr-overdue-bucket">
      <button
        type="button"
        onClick={() => onToggle('__overdue__')}
        data-testid="edr-overdue-toggle"
        className="flex w-full items-center gap-2.5 rounded-lg border-2 border-rose-300 bg-rose-50/70 px-3.5 py-2.5 text-left transition-colors hover:bg-rose-50"
      >
        <AlertTriangle size={16} className="shrink-0 text-rose-500" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-rose-700">Overdue deliveries</span>
          <span className="block text-[11px] text-rose-500/90">Past due &amp; not yet received — carried to today</span>
        </span>
        <CountPills orders={overdue.length} pages={distinctPages(overdue)} usesRef={usesRef} danger />
        <ChevronDown size={16} className={`shrink-0 text-rose-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2">
          <GroupedList groups={groups} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} danger showEta canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        </div>
      )}
    </div>
  );
}


// Mini month calendar for the Daily / Weekly views. Clicking a day re-anchors
// the report (daily -> that day; weekly -> that day's week). "Today / This week"
// jumps to the current day (daily) or current week (weekly).
function MiniCalendar({ anchor, mode, dayTotals, onPickDate }) {
  const [cursor, setCursor] = useState(anchor);
  useEffect(() => { setCursor(anchor); }, [anchor]);
  const cur = parseISO(cursor); cur.setDate(1);
  const monthIdx = cur.getMonth();
  const startPad = cur.getDay(); // Sunday-first
  const gridStart = new Date(cur); gridStart.setDate(cur.getDate() - startPad);
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const weeks = Array.from({ length: 6 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
  const todayISO = toISO(new Date());
  const selWeekStart = mode === 'weekly' ? weekStartOf(anchor) : null;
  const selWeekEnd = selWeekStart ? addDays(selWeekStart, 6) : null;
  const inSelWeek = (iso) => selWeekStart && iso >= selWeekStart && iso <= selWeekEnd;
  const shiftMonth = (n) => setCursor((c) => addMonths(c, n));
  return (
    <div className="w-full rounded-xl border border-[var(--divider)] bg-white p-3 shadow-sm lg:w-[286px] lg:shrink-0" data-testid="edr-mini-calendar">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[13px] font-semibold text-[var(--ink-navy)]">{MONTHS[monthIdx]} {cur.getFullYear()}</div>
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={() => shiftMonth(-1)} data-testid="edr-mini-prev" aria-label="Previous month" className="grid h-6 w-6 place-items-center rounded text-slate-400 transition-colors hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)]"><ChevronLeft size={14} /></button>
          <button type="button" onClick={() => shiftMonth(1)} data-testid="edr-mini-next" aria-label="Next month" className="grid h-6 w-6 place-items-center rounded text-slate-400 transition-colors hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)]"><ChevronRight size={14} /></button>
        </div>
      </div>
      <button type="button" onClick={() => onPickDate(todayISO)} data-testid="edr-mini-today"
        className="mb-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-[var(--divider)] bg-[var(--paper-warm)]/60 py-1 text-[11.5px] font-semibold text-[var(--ink-navy)] transition-colors hover:border-[var(--gold-accent)] hover:bg-[var(--jewellery-bg)]">
        <CalendarClock size={12} className="text-[var(--gold-accent)]" /> {mode === 'weekly' ? 'This week' : 'Today'}
      </button>
      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((w) => <div key={w} className="py-1 text-center text-[9.5px] font-semibold uppercase tracking-wide text-slate-400">{w[0]}</div>)}
      </div>
      <div className="space-y-0.5">
        {weeks.map((week, wi) => {
          const weekSel = mode === 'weekly' && inSelWeek(toISO(week[0]));
          return (
            <div key={wi}
              className={`grid grid-cols-7 gap-0.5 rounded-md ${mode === 'weekly' ? 'cursor-pointer transition-colors hover:bg-[var(--paper-warm)]' : ''} ${weekSel ? 'bg-[var(--jewellery-bg)] ring-1 ring-inset ring-[var(--gold-accent)]/50' : ''}`}
              onClick={mode === 'weekly' ? () => onPickDate(toISO(week[0])) : undefined}
              data-testid={mode === 'weekly' ? `edr-mini-week-${toISO(week[0])}` : undefined}
            >
              {week.map((d) => {
                const iso = toISO(d);
                const inMonth = d.getMonth() === monthIdx;
                const isToday = iso === todayISO;
                const daySel = mode === 'daily' && iso === anchor;
                const hasData = !!(dayTotals[iso] && dayTotals[iso].orders);
                return (
                  <button key={iso} type="button"
                    onClick={(e) => { if (mode === 'weekly') e.stopPropagation(); onPickDate(iso); }}
                    data-testid={`edr-mini-day-${iso}`}
                    className={`relative grid h-7 place-items-center rounded text-[11.5px] transition-colors
                      ${daySel ? 'bg-[var(--ink-navy)] font-bold text-white'
                        : isToday ? 'font-bold text-[var(--gold-accent)] hover:bg-[var(--paper-warm)]'
                        : inMonth ? 'text-[var(--ink-navy)] hover:bg-[var(--paper-warm)]'
                        : 'text-slate-300 hover:bg-[var(--paper-warm)]'}`}
                  >
                    {d.getDate()}
                    {hasData && !daySel && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-[var(--gold-accent)]" />}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyView({ anchor, orders, dayTotals, groupBy, usesRef, refLabel, refLabelShort, expanded, onToggle, onPage, noDate, onPickDate, overdue, today, canEdit, onUpdateCompletion }) {
  const dayOrders = orders.filter((o) => o.estimated_completion === anchor);
  const groups = useMemo(() => pivot(dayOrders, groupBy), [dayOrders, groupBy]);
  const totals = dayTotals[anchor] || { orders: 0, pages: 0 };
  const showOverdue = anchor === today;
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start" data-testid="edr-daily">
      <div className="min-w-0 flex-1 space-y-3">
        <SummaryBanner icon={CalendarClock} title={fmtLong(anchor)} subtitle="Expected delivery — this day" orders={totals.orders} pages={totals.pages} usesRef={usesRef} />
        {showOverdue && (
          <OverdueBucket overdue={overdue} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        )}
        <GroupedList groups={groups} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy}
          emptyHint="No order forms are expected to deliver on this day." canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        <NoDateBucket noDate={noDate} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
      </div>
      <MiniCalendar anchor={anchor} mode="daily" dayTotals={dayTotals} onPickDate={onPickDate} />
    </div>
  );
}

function WeeklyView({ anchor, orders, dayTotals, groupBy, usesRef, refLabel, refLabelShort, expanded, onToggle, onPage, noDate, onPickDate, overdue, today, canEdit, onUpdateCompletion }) {
  const start = weekStartOf(anchor); // Sunday
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  // A single day inside the week can be focused by clicking its tile; the
  // grouped section below then shows just that day. Cleared when the week changes.
  const [selectedDay, setSelectedDay] = useState(null);
  useEffect(() => { setSelectedDay(null); }, [start]);
  const weekOrders = orders.filter((o) => o.estimated_completion && o.estimated_completion >= start && o.estimated_completion <= days[6]);
  const shownOrders = selectedDay ? weekOrders.filter((o) => o.estimated_completion === selectedDay) : weekOrders;
  const groups = useMemo(() => pivot(shownOrders, groupBy), [shownOrders, groupBy]);
  const todayISO = toISO(new Date());
  const showOverdue = today >= start && today <= days[6];
  const weekTitle = `${fmtShort(start)} — ${fmtShort(days[6])}, ${parseISO(anchor).getFullYear()}`;
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start" data-testid="edr-weekly">
      <div className="min-w-0 flex-1 space-y-3">
        {/* Week total (order forms + distinct page numbers expected this week) */}
        <SummaryBanner icon={CalendarRange} title={weekTitle} subtitle="Expected delivery — this week" orders={weekOrders.length} pages={distinctPages(weekOrders)} usesRef={usesRef} />

        {/* Per-day totals strip — click a tile to focus that single day */}
        <div className="grid grid-cols-7 gap-1.5" data-testid="edr-week-strip">
          {days.map((d, i) => {
            const t = dayTotals[d] || { orders: 0, pages: 0 };
            const active = d === todayISO;
            const sel = d === selectedDay;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setSelectedDay((prev) => (prev === d ? null : d))}
                data-testid={`edr-week-day-${d}`}
                aria-pressed={sel}
                title={`Show ${fmtLong(d)}`}
                className={`rounded-lg border px-2 py-2 text-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold-accent)]
                  ${sel ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] shadow-sm'
                    : active ? 'border-[var(--gold-accent)] bg-[var(--jewellery-bg)] hover:border-[var(--gold-accent)]'
                    : 'border-[var(--divider)] bg-white hover:border-[var(--gold-accent)] hover:bg-[var(--paper-warm)]'}`}
              >
                <div className={`text-[10px] font-semibold uppercase tracking-wide ${sel ? 'text-white/70' : 'text-slate-400'}`}>{WEEKDAYS[i]}</div>
                <div className={`text-[15px] font-bold ${sel ? 'text-white' : 'text-[var(--ink-navy)]'}`}>{parseISO(d).getDate()}</div>
                <div className="mt-1 flex items-center justify-center gap-1 text-[10.5px]">
                  <span className={`inline-flex items-center gap-0.5 font-semibold ${sel ? 'text-white' : 'text-[var(--jewellery-icon)]'}`}><Package size={9} />{t.orders}</span>
                  {usesRef && <span className={`inline-flex items-center gap-0.5 font-semibold ${sel ? 'text-amber-200' : 'text-indigo-600'}`}><Hash size={9} />{t.pages}</span>}
                </div>
              </button>
            );
          })}
        </div>

        {selectedDay && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--gold-accent)]/40 bg-[var(--jewellery-bg)] px-3 py-1.5 text-[12px]" data-testid="edr-week-dayfilter">
            <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--ink-navy)]"><CalendarClock size={13} className="text-[var(--gold-accent)]" /> Showing {fmtLong(selectedDay)}</span>
            <button type="button" onClick={() => setSelectedDay(null)} data-testid="edr-week-dayfilter-clear"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--divider)] bg-white px-2 py-1 text-[11.5px] font-medium text-slate-600 transition-colors hover:border-[var(--gold-accent)] hover:text-[var(--ink-navy)]">
              Show whole week
            </button>
          </div>
        )}

        {showOverdue && (
          <OverdueBucket overdue={overdue} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        )}

        <GroupedList groups={groups} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy}
          emptyHint={selectedDay ? 'No order forms are expected to deliver on this day.' : 'No order forms are expected to deliver this week.'} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
        <NoDateBucket noDate={noDate} usesRef={usesRef} refLabel={refLabel} refLabelShort={refLabelShort} expanded={expanded} onToggle={onToggle} onPage={onPage} groupBy={groupBy} canEdit={canEdit} onUpdateCompletion={onUpdateCompletion} />
      </div>
      <MiniCalendar anchor={anchor} mode="weekly" dayTotals={dayTotals} onPickDate={onPickDate} />
    </div>
  );
}

function MonthlyView({ anchor, orders, dayTotals, usesRef, noDateCount, onPickDay, overdueCounts, today }) {
  const first = parseISO(anchor); first.setDate(1);
  const monthIdx = first.getMonth();
  const startPad = first.getDay(); // Sunday-first
  const gridStart = new Date(first); gridStart.setDate(first.getDate() - startPad);
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const todayISO = toISO(new Date());
  const hasOverdueGlobal = !!(overdueCounts && overdueCounts.orders > 0);

  // Month total — orders delivered within this calendar month + distinct pages.
  const monthOrders = orders.filter((o) => {
    if (!o.estimated_completion) return false;
    const d = parseISO(o.estimated_completion);
    return d.getFullYear() === first.getFullYear() && d.getMonth() === monthIdx;
  });
  const monthTitle = `${MONTHS[monthIdx]} ${first.getFullYear()}`;

  return (
    <div className="space-y-3" data-testid="edr-monthly">
      {/* Month total (order forms + distinct page numbers expected this month) */}
      <SummaryBanner icon={CalendarDays} title={monthTitle} subtitle="Expected delivery — this month" orders={monthOrders.length} pages={distinctPages(monthOrders)} usesRef={usesRef} />

      <div className="overflow-hidden rounded-xl border border-[var(--divider)] bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b border-[var(--divider)] bg-[var(--paper-warm)]">
          {WEEKDAYS.map((w) => <div key={w} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const iso = toISO(d);
            const inMonth = d.getMonth() === monthIdx;
            const t = dayTotals[iso];
            const isToday = iso === todayISO;
            const hasOverdue = isToday && inMonth && hasOverdueGlobal;
            return (
              <button
                key={i}
                type="button"
                onClick={() => onPickDay(iso)}
                data-testid={`edr-month-day-${iso}`}
                className={`min-h-[92px] border-b border-r border-[var(--divider)] p-1.5 text-left transition-colors ${(i % 7) === 6 ? 'border-r-0' : ''} ${hasOverdue ? 'bg-rose-50/50 ring-1 ring-inset ring-rose-300 hover:bg-rose-50' : inMonth ? 'bg-white hover:bg-[var(--paper-warm)]' : 'bg-slate-50/60'}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`grid h-6 w-6 place-items-center rounded-full text-[12px] font-semibold ${isToday ? 'bg-[var(--ink-navy)] text-white' : inMonth ? 'text-[var(--ink-navy)]' : 'text-slate-300'}`}>{d.getDate()}</span>
                </div>
                {t && inMonth && (
                  <div className="mt-1.5 space-y-1">
                    <div className="inline-flex w-full items-center gap-1 rounded bg-[var(--jewellery-bg)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--jewellery-icon)]"><Package size={10} /> {t.orders} <span className="font-normal text-[10px] text-slate-400">orders</span></div>
                    {usesRef && <div className="inline-flex w-full items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700"><Hash size={10} /> {t.pages} <span className="font-normal text-[10px] text-indigo-400">pages</span></div>}
                  </div>
                )}
                {hasOverdue && (
                  <div className="mt-1 space-y-1" data-testid="edr-month-overdue">
                    <div className="inline-flex w-full items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700"><AlertTriangle size={10} /> {overdueCounts.orders} <span className="font-normal text-[10px] text-rose-500">overdue</span></div>
                    {usesRef && overdueCounts.pages > 0 && <div className="inline-flex w-full items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700"><Hash size={10} /> {overdueCounts.pages} <span className="font-normal text-[10px] text-rose-400">pages</span></div>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {hasOverdueGlobal && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-300 bg-rose-50/70 px-3.5 py-2 text-[12.5px] font-medium text-rose-700" data-testid="edr-month-overdue-note">
          <AlertTriangle size={14} className="text-rose-500" /> {overdueCounts.orders} order form{overdueCounts.orders === 1 ? '' : 's'} overdue — carried to today ({fmtShort(today)})
        </div>
      )}
      {noDateCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-3.5 py-2 text-[12.5px] font-medium text-amber-800" data-testid="edr-month-nodate">
          <CircleSlash size={14} className="text-amber-500" /> {noDateCount} order form{noDateCount === 1 ? '' : 's'} have no delivery date set
        </div>
      )}
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-400"><CalendarDays size={13} /> Tip: click any day to open its daily breakdown.</div>
    </div>
  );
}
